import React, { FC, useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../api';
import { ReactComponent as LoadingRingsIcon } from '../../../icons/loading-rings.svg';
import './campaign-activity-notice.scss';
import SidewaysLoading from './SidewaysLoading';

interface ColorVariants {
  base: string;
  midDarker: string;
  darker: string;
  lighter: string;
}

interface CampaignActivityNoticeProps {
  campaignId: string;
  isThreadLoading: boolean;
  hasUserSentMessages: boolean;
  colorVariants?: ColorVariants | null;
}

interface CampaignStatus {
  autopilot: boolean;
  draft: boolean;
}

const CampaignActivityNotice: FC<CampaignActivityNoticeProps> = ({
  campaignId,
  isThreadLoading,
  hasUserSentMessages,
  colorVariants,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [campaignStatus, setCampaignStatus] = useState<CampaignStatus | null>(
    null,
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCampaignStatus = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await api.get(`/campaigns/${campaignId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.success) {
          setCampaignStatus({
            autopilot: response.data.campaign.autopilot,
            draft: response.data.campaign.draft,
          });
        }
      } catch (error) {
        console.error('Error fetching campaign status:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaignStatus();
  }, [campaignId, getAccessTokenSilently]);

  // Don't show anything if:
  // - Still loading campaign data
  // - User has already sent messages
  // - Thread is not loading
  // - Campaign is live (autopilot is on)
  if (
    loading ||
    hasUserSentMessages ||
    !isThreadLoading ||
    campaignStatus?.autopilot
  ) {
    return null;
  }

  return (
    <div className='campaign-activity-wrapper'>
      <div
        className='campaign-activity-notice'
        style={{
          borderColor: colorVariants?.midDarker,
        }}
      >
        <div className='notice-content'>
          <p className='notice-heading'>
            <SidewaysLoading />
            I'm working on your campaign
          </p>
          <p className='notice-subtext'>
            I'll notify you when I need your approval for messaging and
            strategy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CampaignActivityNotice;
